import { Routes as PermissionRoute } from 'app/shared/models';
import { NavigationService, PermissionsService } from 'app/shared/services';
import { catchError, map, of, switchMap } from 'rxjs';

import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation';

interface NavigationLink {
  link: string;
  scope: string;
}

const permissionRouteByScope: Record<string, PermissionRoute> = {
  '/readiness': PermissionRoute.Readiness,
  '/registers/business-plans': PermissionRoute.BusinessPlans,
  '/registers/charge-points': PermissionRoute.Chargers,
  '/registers/circuits': PermissionRoute.Circuits,
  '/registers/customers': PermissionRoute.Customers,
  '/registers/location-categories': PermissionRoute.LocationCategories,
  '/registers/locations': PermissionRoute.Locations,
  '/registers/partners': PermissionRoute.Partners,
  '/registers/tariffs': PermissionRoute.Tariffs,
  '/registers/users': PermissionRoute.Users,
  '/registers/vehicle-models': PermissionRoute.VehicleModels,
  '/registers/vehicles': PermissionRoute.Vehicles,
  '/reports/charge-point-problems': PermissionRoute.BrokenChargers,
};

const normalizePath = (value: string): string => {
  const path = value.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
  const normalizedPath = path ? `/${path}` : '/';

  return normalizedPath === '/reports/avability'
    ? '/reports/availability'
    : normalizedPath;
};

const getPermissionScope = (link: string): string => {
  const path = normalizePath(link);
  return path.endsWith('/list') ? path.slice(0, -'/list'.length) : path;
};

const getNavigationLinks = (
  items: FuseNavigationItem[]
): NavigationLink[] =>
  items.flatMap((item) => {
    const children = item.children ?? [];

    if (children.length > 0) {
      return getNavigationLinks(children);
    }

    return item.link
      ? [
          {
            link: normalizePath(item.link),
            scope: getPermissionScope(item.link),
          },
        ]
      : [];
  });

const isCreationPath = (requestedPath: string, scope: string): boolean => {
  const childPath = requestedPath.slice(scope.length).replace(/^\/+/, '');

  return childPath === 'new';
};

export const PermissionGuard: CanActivateChildFn = (_route, state) => {
  const navigationService = inject(NavigationService);
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);

  return navigationService.get().pipe(
    switchMap(({ success, data }) => {
      if (!success) {
        return of(false);
      }

      const links = getNavigationLinks(data ?? []);
      const requestedPath = normalizePath(state.url);
      const matchingLink = links
        .filter(
          ({ scope }) =>
            requestedPath === scope || requestedPath.startsWith(`${scope}/`)
        )
        .sort((a, b) => b.scope.length - a.scope.length)[0];

      if (!matchingLink) {
        return of(
          links.length > 0 ? router.parseUrl(links[0].link) : false
        );
      }

      const permissionRoute = permissionRouteByScope[matchingLink.scope];

      if (!isCreationPath(requestedPath, matchingLink.scope) || !permissionRoute) {
        return of(true);
      }

      return permissionsService.getByRoute(permissionRoute).pipe(
        map(({ success: permissionSuccess, data: permission }) => {
          const allowed =
            permissionSuccess &&
            !!permission &&
            permission.canAdd;

          return allowed ? true : router.parseUrl(matchingLink.scope);
        })
      );
    }),
    catchError(() => of(false))
  );
};
